import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { updateUser } from "../../redux/user/userAction";
import { toast } from "react-toastify";
import { motion, AnimatePresence } from "framer-motion";
import ReactFlagsSelect from "react-flags-select";
import DatePicker from "react-datepicker";
import moment from "moment";
import "react-datepicker/dist/react-datepicker.css";
import "./style.css";

const ProfileEdit = ({ shouldEdit, close }) => {
  const useSecondStep = shouldEdit === "workspace" || shouldEdit === "hobbies";
  const dispatch = useDispatch();
  const { loading, userData } = useSelector((state) => state.user);
  const [step, setStep] = useState(useSecondStep ? 1 : 0);

  const userNameRef = useRef(null)
  const addressRef = useRef(null)
  const workSpace = useRef(null)
  const hubbies = useRef(null)

  const [data, setUserData] = useState({
    firstName: userData.firstName || "",
    lastName: userData.lastName || "",
    UserName: userData.UserName || "",
    email: userData.email || "",
    phoneNumber: userData.phoneNumber || "",
    address: userData.address || "",
    Country: userData.Country || "",
    WorkSpace: userData.WorkSpace || "",
    RelationshipStatus: userData.RelationshipStatus || "",
    Hobbies: userData.Hobbies || "",
    Bio: userData.Bio || "",
    dateofBirth: userData.dateofBirth || "",
    gender: userData.gender || "",
  });


  const handleChange = (key, value) => {
    setUserData((prev) => ({ ...prev, [key]: value }));
  };


  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const result = await dispatch(updateUser(data)).unwrap();
      if (result) {
        toast.success("Profile updated successfully!");
        close();
      }
    } catch (error) {
      toast.error(error.message || "Something went wrong.");
    }
  };


  useEffect(() => {
    if (!shouldEdit) return;
    switch (shouldEdit) {
      case "username":
        userNameRef.current?.focus();
        break;
      case "address":
        addressRef.current?.focus();
        break;
      case "workspace":
        workSpace.current?.focus();
        break;
      case "hobbies":
        hubbies.current?.focus();
        break;
      default:
        break;
    }
  }, [shouldEdit]);

  return (
    <div className="bg-customDark w-full max-w-screen-md mx-auto rounded-xl shadow-md p-4 sm:p-6 box-border">
      <form onSubmit={handleSubmit} className="w-full">
        <AnimatePresence mode="wait">
          <motion.div
            key={step}
            initial={{ opacity: 0, x: 100 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -100 }}
            transition={{ duration: 0.3 }}
            className="grid gap-5 sm:gap-6 min-h-[220px]">

            {step === 0 && (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                  {/* First Name */}
                  <div className="flex flex-col gap-1 min-w-0">
                    <label className="text-xs sm:text-sm text-gray-300">First Name</label>
                    <input
                      type="text"
                      value={data.firstName}
                      onChange={(e) => handleChange("firstName", e.target.value)}
                      className="bg-customGray text-white px-3 py-2 sm:px-4 sm:py-3 rounded-md text-sm sm:text-base w-full min-w-0"
                    />
                  </div>

                  {/* Last Name */}
                  <div className="flex flex-col gap-1 min-w-0">
                    <label className="text-xs sm:text-sm text-gray-300">Last Name</label>
                    <input
                      type="text"
                      value={data.lastName}
                      onChange={(e) => handleChange("lastName", e.target.value)}
                      className="bg-customGray text-white px-3 py-2 sm:px-4 sm:py-3 rounded-md text-sm sm:text-base w-full min-w-0"
                    />
                  </div>

                  {/* Username */}
                  <div className="flex flex-col gap-1 min-w-0">
                    <label className="text-xs sm:text-sm text-gray-300">Username</label>
                    <input
                      type="text"
                      ref={userNameRef}
                      placeholder="Username"
                      value={data.UserName}
                      onChange={(e) => handleChange("UserName", e.target.value)}
                      className="bg-customGray text-white px-3 py-2 sm:px-4 sm:py-3 rounded-md text-sm sm:text-base w-full min-w-0"
                    />
                  </div>

                  {/* Phone Number */}
                  <div className="flex flex-col gap-1 min-w-0">
                    <label className="text-xs sm:text-sm text-gray-300">Phone Number</label>
                    <input
                      type="text"
                      value={data.phoneNumber}
                      onChange={(e) => handleChange("phoneNumber", e.target.value)}
                      className="bg-customGray text-white px-3 py-2 sm:px-4 sm:py-3 rounded-md text-sm sm:text-base w-full min-w-0"
                    />
                  </div>

                  {/* Address */}
                  <div className="flex flex-col gap-1 min-w-0">
                    <label className="text-xs sm:text-sm text-gray-300">Address</label>
                    <input
                      type="text"
                      value={data.address}
                      ref={addressRef}
                      placeholder="Address"
                      onChange={(e) => handleChange("address", e.target.value)}
                      className="bg-customGray text-white px-3 py-2 sm:px-4 sm:py-3 rounded-md text-sm sm:text-base w-full min-w-0"
                    />
                  </div>

                  {/* Country */}
                  <div className="flex flex-col gap-1 min-w-0">
                    <label className="text-xs sm:text-sm text-gray-300">Country</label>
                    <div className="bg-customGray text-white px-2 py-1 sm:px-4 sm:py-2 rounded-md w-full min-w-0">
                      {/* ensure ReactFlagsSelect can shrink inside its parent */}
                      <div className="min-w-0">
                        <ReactFlagsSelect
                          selected={data.Country}
                          onSelect={(code) => handleChange("Country", code)}
                          searchable
                          className="text-black w-full"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}

            {step === 1 && (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                  {/* Workspace */}
                  <div className="flex flex-col gap-1 min-w-0">
                    <label className="text-xs sm:text-sm text-gray-300">Profession</label>
                    <input
                      type="text"
                      value={data.WorkSpace}
                      ref={workSpace}
                      placeholder="Workspace"
                      onChange={(e) => handleChange("WorkSpace", e.target.value)}
                      className="bg-customGray text-white px-3 py-2 sm:px-4 sm:py-3 rounded-md text-sm sm:text-base w-full min-w-0"
                    />
                  </div>

                  {/* Relationship Status */}
                  <div className="flex flex-col gap-1 min-w-0">
                    <label className="text-xs sm:text-sm text-gray-300">Relationship Status</label>
                    <input
                      type="text"
                      value={data.RelationshipStatus}
                      onChange={(e) => handleChange("RelationshipStatus", e.target.value)}
                      className="bg-customGray text-white px-3 py-2 sm:px-4 sm:py-3 rounded-md text-sm sm:text-base w-full min-w-0"
                    />
                  </div>

                  {/* Hobbies */}
                  <div className="flex flex-col gap-1 min-w-0">
                    <label className="text-xs sm:text-sm text-gray-300">Hobbies</label>
                    <input
                      type="text"
                      value={data.Hobbies}
                      ref={hubbies}
                      placeholder="Hobbies"
                      onChange={(e) => handleChange("Hobbies", e.target.value)}
                      className="bg-customGray text-white px-3 py-2 sm:px-4 sm:py-3 rounded-md text-sm sm:text-base w-full min-w-0"
                    />
                  </div>

                  {/* Gender */}
                  <div className="flex flex-col gap-1 min-w-0">
                    <label className="text-xs sm:text-sm text-gray-300">Gender</label>
                    <select
                      value={data.gender}
                      onChange={(e) => handleChange("gender", e.target.value)}
                      className="bg-customGray text-white px-3 py-2 sm:px-4 sm:py-3 rounded-md appearance-none text-sm sm:text-base w-full min-w-0"
                    >
                      <option value="">Select Gender</option>
                      <option value="male">Male</option>
                      <option value="female">Female</option>
                    </select>
                  </div>

                  {/* Date of Birth */}
                  <div className="flex flex-col gap-1 min-w-0">
                    <label className="text-xs sm:text-sm text-gray-300">Date of Birth</label>
                    <DatePicker
                      selected={data.dateofBirth ? new Date(data.dateofBirth) : null}
                      onChange={(date) =>
                        handleChange("dateofBirth", date ? moment(date).format("YYYY-MM-DD") : "")
                      }
                      showYearDropdown
                      scrollableYearDropdown={false}
                      yearDropdownItemNumber={100}
                      dateFormat="dd/MM/yyyy"
                      placeholderText="Select date of birth"
                      wrapperClassName="w-full min-w-0"
                      className="bg-customGray text-white px-3 py-2 sm:px-4 sm:py-3 rounded-md text-sm sm:text-base w-full min-w-0"
                    />
                  </div>
                </div>

                {/* Bio */}
                <div className="flex flex-col gap-1 mt-4 sm:mt-6">
                  <label className="text-xs sm:text-sm text-gray-300">Bio</label>
                  <textarea
                    value={data.Bio}
                    onChange={(e) => handleChange("Bio", e.target.value)}
                    className="w-full h-24 sm:h-28 bg-customGray text-white px-3 py-2 sm:px-4 sm:py-3 rounded-md resize-y text-sm sm:text-base min-w-0"
                  />
                </div>
              </>
            )}
          </motion.div>
        </AnimatePresence>

        {/* Buttons */}
        <div className="flex flex-wrap justify-between gap-3 mt-6 sm:mt-8">
          <button
            key={'14543'}
            type="button"
            onClick={() => setStep((prev) => Math.max(prev - 1, 0))}
            disabled={loading}
            className={`px-5 py-2 rounded-md bg-customGray shadow-sm text-sm sm:text-base ${step === 0 ? "opacity-40" : ""}`}
          >
            Previous
          </button>

          {step < 1 ? (
            <button
              key={'2'}
              type="button"
              onClick={() => setStep((prev) => prev + 1)}
              disabled={loading}
              className="px-5 py-2 rounded-md bg-customGray shadow-sm text-sm sm:text-base"
            >
              Next
            </button>
          ) : (
            <button
              key={'3'}
              type="submit"
              className="px-5 py-2 rounded-md bg-[#BE9524] text-black text-sm sm:text-base"
              disabled={loading}
            >
              {loading ? "Saving..." : "Save Profile"}
            </button>
          )}
        </div>
      </form>
    </div>
  );

};

export default ProfileEdit;
